import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';

export interface CreditState {
  plan: string;
  credits: number;
  documentsCreated: number;
  documentsAllowed: boolean;
  planStart: string | null;
  planEnd: string | null;
  loading: boolean;
}

const DEFAULTS: CreditState = {
  plan: 'free',
  credits: 0,
  documentsCreated: 0,
  documentsAllowed: true,
  planStart: null,
  planEnd: null,
  loading: true,
};

const parseNonNegativeIntegerAttribute = (value: string | null | undefined): number => {
  const text = (value ?? '').trim();
  if (!/^\d+$/.test(text)) return 0;
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) ? parsed : 0;
};

export const useCredits = () => {
  const { user } = useAuth();
  const [state, setState] = useState<CreditState>(DEFAULTS);

  const fetch = useCallback(async () => {
    if (!user) {
      setState({ ...DEFAULTS, loading: false });
      return;
    }
    const { data } = await supabase
      .from('profile_attributes')
      .select('attribute_name, attribute_value')
      .eq('user_id', user.id);

    const rows = (data ?? []) as Array<{ attribute_name: string; attribute_value: string | null }>;
    const map = new Map<string, string>(rows.map((row) => [row.attribute_name, row.attribute_value ?? '']));
    setState({
      plan: map.get('plan') ?? 'free',
      credits: parseNonNegativeIntegerAttribute(map.get('credits')),
      documentsCreated: parseNonNegativeIntegerAttribute(map.get('documents_created')),
      documentsAllowed: true,
      planStart: map.get('plan_start') ?? null,
      planEnd: map.get('plan_end') ?? null,
      loading: false,
    });
  }, [user]);

  useEffect(() => {
    fetch();
    if (!user) return;

    // Realtime subscription (best-effort; may miss if websocket drops or admin updates bypass)
    const channelSuffix = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(`profile_attributes:${user.id}:${channelSuffix}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'profile_attributes', filter: `user_id=eq.${user.id}` },
        () => fetch()
      )
      .subscribe();

    // Safety net: refetch on tab focus / visibility change (catches admin-side updates)
    const onFocus = () => fetch();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetch();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    // Safety net: refetch every 30s while the tab is open
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') fetch();
    }, 30_000);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(intervalId);
    };
  }, [user, fetch]);

  return { ...state, refresh: fetch };
};
